import { Sim } from "../sim/sim";
import { Input } from "./input";
import { Camera } from "../render/camera";
import { getDef } from "../data";
import { drawSprite, paletteFor } from "../render/art";
import { spriteAtlas } from "../render/sprites";
import { settings } from "./settings";
import { OWNER_PLAYER } from "../sim/constants";
import { audio } from "../render/audio";
import { toggleSettings } from "./settingsui";

// DOM overlay HUD (GDD §8.1): resource readouts (the two most important elements),
// build menu, selection panel, minimap. Dark tactical chrome — overlays, not
// layout boxes; nothing looks like default HTML.

const HOTKEYS = "QWERASDF"; // build-menu hotkey hints (first N options)

const CSS = `
#hud { position:fixed; inset:0; pointer-events:none; z-index:10;
  font-family:"Segoe UI","Helvetica Neue",Arial,sans-serif; color:#dfe8ef;
  --panel:#0b1219; --panel2:#0e1720; --line:#1e2c37; --line2:#2a3d4a;
  --cyan:#40e0ff; --amber:#ffb84d; --red:#ff5a3c; --green:#7ce6a0; --dim:#7f96a8; }
#hud .panel { pointer-events:auto; }

/* --- Top resource cluster --- */
#topbar { position:absolute; top:12px; left:12px; display:flex; gap:10px; align-items:stretch; }
.res { position:relative; width:238px; padding:8px 12px 9px; border-radius:9px;
  background:linear-gradient(180deg,#0e1720f2,#0a1017e6); border:1px solid var(--line);
  box-shadow:0 8px 22px -12px #000c, inset 0 1px 0 #ffffff08; overflow:hidden; }
.res::before { content:""; position:absolute; left:0; top:0; bottom:0; width:3px; }
.res.metal::before { background:linear-gradient(#e7edf2,#9fb0bd); }
.res.energy::before { background:linear-gradient(#ffe58a,#f2c14e); }
.res .top { display:flex; align-items:baseline; justify-content:space-between; margin-bottom:3px; }
.res .name { font-size:10px; letter-spacing:3px; color:var(--dim); display:flex; align-items:center; gap:6px; }
.res .name .dot { width:7px; height:7px; border-radius:50%; }
.res.metal .name .dot { background:#cfd8df; }
.res.energy .name .dot { background:#f2c14e; }
.res .cur { font-size:19px; font-weight:600; font-variant-numeric:tabular-nums; letter-spacing:.5px; line-height:1; }
.res .capinline { font-size:11px; font-weight:400; color:var(--dim); }
.res .cap { font-size:11px; color:var(--dim); font-variant-numeric:tabular-nums; }
.res .bar { position:relative; height:7px; margin:6px 0 5px; background:#060b10; border-radius:4px;
  border:1px solid #1a2832; overflow:hidden; }
.res .fill { position:absolute; inset:0; width:0%; border-radius:3px; transition:width .12s linear; }
.res.metal .fill { background:linear-gradient(90deg,#aebac4,#eef3f7); }
.res.energy .fill { background:linear-gradient(90deg,#e0af3e,#ffe58a); }
.res .foot { display:flex; justify-content:space-between; font-size:11px; font-variant-numeric:tabular-nums; }
.res .delta { font-weight:600; }
.res .delta.pos { color:var(--green); } .res .delta.neg { color:var(--red); }
.res .warn { position:absolute; top:7px; right:10px; font-size:10px; letter-spacing:1px; font-weight:700;
  opacity:0; transition:.15s; }
.res.stall .warn.s { opacity:1; color:var(--red); } .res.over .warn.o { opacity:1; color:var(--amber); }
.res.stall { border-color:#5a2a24; animation:pulseRed 0.7s infinite; }
.res.over { border-color:#5a4620; animation:pulseAmber 0.9s infinite; }
@keyframes pulseRed { 0%,100%{box-shadow:0 8px 22px -12px #000c, 0 0 0 0 #ff5a3c00} 50%{box-shadow:0 8px 22px -12px #000c, 0 0 14px 1px #ff5a3c66} }
@keyframes pulseAmber { 0%,100%{box-shadow:0 8px 22px -12px #000c, 0 0 0 0 #ffb84d00} 50%{box-shadow:0 8px 22px -12px #000c, 0 0 14px 1px #ffb84d55} }

/* --- Top-right status --- */
#status { position:absolute; top:12px; right:12px; display:flex; gap:8px; align-items:center; }
#clock { font-variant-numeric:tabular-nums; font-size:15px; letter-spacing:1px; color:#cdd9e2;
  background:#0e1720cc; border:1px solid var(--line); border-radius:8px; padding:7px 12px; min-width:64px; text-align:center; }
#wind { font-size:10px; color:var(--dim); letter-spacing:1px; margin-top:2px; }
.iconbtn { pointer-events:auto; width:38px; height:38px; display:flex; align-items:center; justify-content:center;
  background:#0e1720cc; border:1px solid var(--line); border-radius:8px; cursor:pointer; color:#c5d2dc; transition:.12s; }
.iconbtn:hover { border-color:var(--cyan); color:#e8f4ff; }
.iconbtn svg { width:18px; height:18px; }

/* --- Selection panel --- */
#selpanel { position:absolute; left:12px; bottom:216px; display:none; gap:8px; align-items:flex-end; }
.selcard { position:relative; width:56px; background:#0e1720e6; border:1px solid var(--line); border-radius:8px;
  padding:5px 4px 4px; text-align:center; }
.selcard canvas { display:block; margin:0 auto; }
.selcard .n { position:absolute; top:2px; right:4px; font-size:11px; font-weight:700; color:var(--cyan);
  font-variant-numeric:tabular-nums; text-shadow:0 0 4px #000; }
.selcard .nm { font-size:8px; letter-spacing:.5px; color:#aebcc7; margin-top:1px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
#selmeta { align-self:flex-end; font-size:11px; color:var(--dim); padding-bottom:4px; font-variant-numeric:tabular-nums; }

/* --- Build menu --- */
#buildmenu { position:absolute; left:12px; bottom:12px; display:grid; grid-template-columns:repeat(4,60px); gap:6px; }
.btn { pointer-events:auto; position:relative; width:60px; height:60px; border-radius:8px; cursor:pointer;
  background:linear-gradient(180deg,#0f1a24,#0b131b); border:1px solid var(--line);
  display:flex; flex-direction:column; align-items:center; transition:.1s; box-shadow:0 4px 12px -8px #000; }
.btn:hover { border-color:var(--cyan); transform:translateY(-2px); box-shadow:0 8px 18px -8px #000c, 0 0 0 1px #40e0ff44; }
.btn canvas { margin-top:4px; }
.btn .nm { font-size:8px; line-height:1; color:#c5d2dc; letter-spacing:.3px; }
.btn .cost { position:absolute; bottom:2px; left:3px; right:3px; display:flex; justify-content:space-between; font-size:8px; font-variant-numeric:tabular-nums; }
.btn .cM { color:#dbe3ea; } .btn .cE { color:#f2c14e; }
.btn .q { position:absolute; top:1px; right:4px; font-size:11px; color:var(--cyan); font-weight:bold; text-shadow:0 0 4px #000; }
.btn .hk { position:absolute; top:1px; left:4px; font-size:8px; color:#5f7688; font-weight:700; }
.tooltip { position:fixed; z-index:60; pointer-events:none; max-width:220px; background:#0c1219f5; border:1px solid var(--line2);
  border-radius:8px; padding:9px 11px; font-size:11px; color:#c5d2dc; box-shadow:0 12px 30px -10px #000e; display:none; line-height:1.5; }
.tooltip b { color:#e8f4ff; font-size:12px; letter-spacing:.5px; }
.tooltip .tstat { color:var(--dim); }
.tooltip .tval { color:#dbe3ea; font-variant-numeric:tabular-nums; }

/* --- Minimap --- */
#mmwrap { position:absolute; right:12px; bottom:12px; padding:6px; border-radius:10px;
  background:#0a1017e6; border:1px solid var(--line2); box-shadow:0 10px 30px -12px #000d; pointer-events:auto; }
#mmwrap .mmlabel { position:absolute; top:-9px; left:10px; font-size:8px; letter-spacing:2px; color:var(--dim);
  background:#0a1017; padding:0 5px; border-radius:3px; }
#minimap { display:block; border-radius:5px; }

/* --- Hint --- */
#hint { position:absolute; right:14px; bottom:230px; width:210px; font-size:10px; color:#5f7688; text-align:right; line-height:1.6;
  pointer-events:none; }
#pauseind { position:absolute; top:64px; left:50%; transform:translateX(-50%); font-size:12px; letter-spacing:2px;
  color:var(--amber); background:#0e1720cc; border:1px solid #5a4620; padding:6px 16px; border-radius:20px; display:none; }
`;

export class Hud {
  root: HTMLDivElement;
  private sim: Sim;
  private input: Input;
  private cam: Camera;
  private mm: HTMLCanvasElement;
  private mmCtx: CanvasRenderingContext2D;
  private mmScale: number;
  private buildmenu: HTMLDivElement;
  private selpanel: HTMLDivElement;
  private pauseInd: HTMLDivElement;
  private tooltip: HTMLDivElement;
  private menuKey = "";
  private selKey = "";
  private pings: { x: number; y: number; age: number; color: string }[] = [];
  private els: Record<string, HTMLElement> = {};
  private dispMetal = 0;
  private dispEnergy = 0;

  constructor(sim: Sim, input: Input, cam: Camera) {
    this.sim = sim;
    this.input = input;
    this.cam = cam;
    if (!document.getElementById("hud-style")) {
      const st = document.createElement("style");
      st.id = "hud-style";
      st.textContent = CSS;
      document.head.appendChild(st);
    }
    this.root = document.createElement("div");
    this.root.id = "hud";
    this.root.innerHTML = `
      <div id="topbar">
        <div class="res metal panel" id="mcard">
          <div class="warn s">⚠ STALL</div><div class="warn o">▲ OVERFLOW</div>
          <div class="top"><span class="name"><span class="dot"></span>METAL</span></div>
          <div class="cur"><span id="mcur">0</span> <span class="capinline" id="mcap"></span></div>
          <div class="bar"><div class="fill" id="mfill"></div></div>
          <div class="foot"><span class="delta" id="mflow">+0/s</span><span class="cap" id="mio"></span></div>
        </div>
        <div class="res energy panel" id="ecard">
          <div class="warn s">⚠ STALL</div><div class="warn o">▲ OVERFLOW</div>
          <div class="top"><span class="name"><span class="dot"></span>ENERGY</span></div>
          <div class="cur"><span id="ecur">0</span> <span class="capinline" id="ecap"></span></div>
          <div class="bar"><div class="fill" id="efill"></div></div>
          <div class="foot"><span class="delta" id="eflow">+0/s</span><span class="cap" id="eio"></span></div>
        </div>
      </div>
      <div id="status">
        <div><div id="clock"><span id="gtime">0:00</span></div><div id="wind" style="text-align:center"></div></div>
        <div class="iconbtn panel" id="pausebtn" title="Pause (Space)">
          <svg viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></svg>
        </div>
        <div class="iconbtn panel" id="setbtn" title="Settings">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M19.1 4.9L17 7M7 17l-2.1 2.1"/></svg>
        </div>
      </div>
      <div id="pauseind">PAUSED — SPACE TO RESUME</div>
      <div id="selpanel" class="panel"></div>
      <div id="buildmenu" class="panel"></div>
      <div id="mmwrap" class="panel"><span class="mmlabel">TACTICAL</span><canvas id="minimap" width="200" height="200"></canvas></div>
      <div id="hint">
        L-drag select · R-click order<br/>A attack-move · S stop<br/>R-click own site/damaged: repair &amp; resume<br/>place: Shift queue · Alt free (no snap)<br/>wheel zoom · edge/mid-drag pan · space pause
      </div>
      <div class="tooltip" id="tooltip"></div>
    `;
    document.getElementById("app")!.appendChild(this.root);

    for (const id of ["mfill", "mcur", "mcap", "mflow", "mio", "efill", "ecur", "ecap", "eflow", "eio", "wind", "gtime", "mcard", "ecard"]) {
      this.els[id] = this.root.querySelector("#" + id) as HTMLElement;
    }
    this.buildmenu = this.root.querySelector("#buildmenu") as HTMLDivElement;
    this.selpanel = this.root.querySelector("#selpanel") as HTMLDivElement;
    this.pauseInd = this.root.querySelector("#pauseind") as HTMLDivElement;
    this.tooltip = this.root.querySelector("#tooltip") as HTMLDivElement;
    this.mm = this.root.querySelector("#minimap") as HTMLCanvasElement;
    this.mmCtx = this.mm.getContext("2d")!;
    this.mmScale = this.mm.width / Math.max(sim.world.gmap.worldW, sim.world.gmap.worldH);

    (this.root.querySelector("#pausebtn") as HTMLElement).onclick = () => {
      this.input.paused = !this.input.paused;
      audio.ui("click");
    };
    (this.root.querySelector("#setbtn") as HTMLElement).onclick = () => toggleSettings(12, 60);
    this.bindMinimap();

    const eco = sim.world.eco[OWNER_PLAYER];
    this.dispMetal = eco.metal;
    this.dispEnergy = eco.energy;
  }

  destroy(): void {
    this.root.remove();
  }

  addPing(x: number, y: number, color: string): void {
    this.pings.push({ x, y, age: 0, color });
    if (this.pings.length > 40) this.pings.shift();
  }

  private bindMinimap(): void {
    const toWorld = (e: MouseEvent) => {
      const r = this.mm.getBoundingClientRect();
      const wx = ((e.clientX - r.left) / r.width) * this.sim.world.gmap.worldW;
      const wy = ((e.clientY - r.top) / r.height) * this.sim.world.gmap.worldH;
      return { wx, wy };
    };
    this.mm.addEventListener("mousedown", (e) => {
      e.preventDefault();
      const { wx, wy } = toWorld(e);
      if (e.button === 0) this.cam.centerOn(wx, wy);
    });
    this.mm.addEventListener("contextmenu", (e) => e.preventDefault());
  }

  update(dt: number): void {
    const w = this.sim.world;
    const eco = w.eco[OWNER_PLAYER];

    // Eased counters (smooth readout).
    const k = 1 - Math.pow(0.001, dt);
    this.dispMetal += (eco.metal - this.dispMetal) * k;
    this.dispEnergy += (eco.energy - this.dispEnergy) * k;

    (this.els.mfill as HTMLElement).style.width = `${Math.min(100, (eco.metal / Math.max(1, eco.metalCap)) * 100)}%`;
    (this.els.efill as HTMLElement).style.width = `${Math.min(100, (eco.energy / Math.max(1, eco.energyCap)) * 100)}%`;
    this.els.mcur.textContent = Math.round(this.dispMetal).toString();
    this.els.ecur.textContent = Math.round(this.dispEnergy).toString();
    this.els.mcap.textContent = `/ ${eco.metalCap.toFixed(0)}`;
    this.els.ecap.textContent = `/ ${eco.energyCap.toFixed(0)}`;

    this.setDelta(this.els.mflow, eco.metalIncome - eco.metalExpense);
    this.setDelta(this.els.eflow, eco.energyIncome - eco.energyExpense);
    this.els.mio.textContent = `+${eco.metalIncome.toFixed(0)} −${eco.metalExpense.toFixed(0)}`;
    this.els.eio.textContent = `+${eco.energyIncome.toFixed(0)} −${eco.energyExpense.toFixed(0)}`;
    this.els.wind.textContent = `WIND ${w.windCurrent.toFixed(0)}`;

    this.setState(this.els.mcard, eco.metalWasted > 0, eco.stalled && eco.metal < eco.metalCap * 0.05);
    this.setState(this.els.ecard, eco.energyWasted > 0, eco.stalled && eco.energy < eco.energyCap * 0.05);

    this.els.gtime.textContent = fmtTime(w.tick / 30);
    this.pauseInd.style.display = this.input.paused ? "block" : "none";

    this.updateBuildMenu();
    this.updateSelPanel();

    for (let i = this.pings.length - 1; i >= 0; i--) {
      this.pings[i].age += dt;
      if (this.pings[i].age > 2) this.pings.splice(i, 1);
    }
    this.drawMinimap();
  }

  private setDelta(el: HTMLElement, v: number): void {
    el.textContent = `${v >= 0 ? "▲ +" : "▼ "}${v.toFixed(0)}/s`;
    el.classList.toggle("pos", v >= 0);
    el.classList.toggle("neg", v < 0);
  }

  private setState(card: HTMLElement, over: boolean, stall: boolean): void {
    card.classList.toggle("over", over && !stall);
    card.classList.toggle("stall", stall);
  }

  private menuMode(): { mode: "build" | "factory" | "none"; defId: string; builderDef: string } {
    const w = this.sim.world;
    let factory = -1;
    let builder = -1;
    for (const e of this.input.selection) {
      if (!w.isAlive(e)) continue;
      const d = w.def(e);
      if (d.role === "factory" && factory < 0) factory = e;
      else if (d.buildPower > 0 && d.buildOptions && builder < 0) builder = e;
    }
    if (factory >= 0) return { mode: "factory", defId: "", builderDef: w.def(factory).id };
    if (builder >= 0) return { mode: "build", defId: "", builderDef: w.def(builder).id };
    return { mode: "none", defId: "", builderDef: "" };
  }

  private updateBuildMenu(): void {
    const m = this.menuMode();
    const key = m.mode + ":" + m.builderDef;
    if (key !== this.menuKey) {
      this.menuKey = key;
      this.rebuildMenu(m.mode, m.builderDef);
    }
    if (m.mode === "factory") {
      const w = this.sim.world;
      const counts: Record<string, number> = {};
      for (const e of this.input.selection) {
        if (w.isAlive(e) && w.def(e).role === "factory") {
          for (const id of w.factoryQueue[e]) counts[id] = (counts[id] ?? 0) + 1;
        }
      }
      for (const btn of Array.from(this.buildmenu.children) as HTMLElement[]) {
        const id = btn.dataset.id!;
        const q = counts[id] ?? 0;
        (btn.querySelector(".q") as HTMLElement).textContent = q > 0 ? String(q) : "";
      }
    }
  }

  private rebuildMenu(mode: "build" | "factory" | "none", builderDef: string): void {
    this.buildmenu.innerHTML = "";
    this.tooltip.style.display = "none";
    if (mode === "none" || !builderDef) return;
    const def = getDef(builderDef);
    const opts = def.buildOptions ?? [];
    opts.forEach((id, idx) => {
      const od = getDef(id);
      const btn = document.createElement("div");
      btn.className = "btn";
      btn.dataset.id = id;
      const cv = document.createElement("canvas");
      cv.width = 40; cv.height = 34;
      const g = cv.getContext("2d")!;
      g.translate(20, 18);
      const scale = Math.min(1.5, 13 / od.radius);
      g.scale(scale, scale);
      if (od.kind !== "building") g.rotate(-Math.PI / 2);
      drawSprite(g, od.role, od.faction, od.radius, paletteFor(od.faction));
      btn.appendChild(cv);
      const nm = document.createElement("div"); nm.className = "nm"; nm.textContent = od.name;
      btn.appendChild(nm);
      const cost = document.createElement("div"); cost.className = "cost";
      cost.innerHTML = `<span class="cM">${od.metalCost}</span><span class="cE">${od.energyCost}</span>`;
      btn.appendChild(cost);
      const q = document.createElement("div"); q.className = "q"; btn.appendChild(q);
      if (idx < HOTKEYS.length) {
        const hk = document.createElement("div"); hk.className = "hk"; hk.textContent = HOTKEYS[idx]; btn.appendChild(hk);
      }
      btn.onclick = (ev) => {
        audio.ui("click");
        if (mode === "build") this.input.startPlacing(id);
        else this.input.queueUnit(id, ev.shiftKey ? 5 : 1);
      };
      btn.oncontextmenu = (ev) => {
        ev.preventDefault();
        if (mode === "factory") { this.input.cancelUnit(id); audio.ui("deny"); }
      };
      btn.onmouseenter = () => this.showTooltip(btn, od, mode);
      btn.onmouseleave = () => { this.tooltip.style.display = "none"; };
      this.buildmenu.appendChild(btn);
    });
  }

  private showTooltip(btn: HTMLElement, od: ReturnType<typeof getDef>, mode: string): void {
    const r = btn.getBoundingClientRect();
    const stats: string[] = [
      `<div><span class="tstat">Cost</span> <span class="tval">${od.metalCost} M · ${od.energyCost} E</span></div>`,
      `<div><span class="tstat">Build</span> <span class="tval">${od.buildTime}s</span> · <span class="tstat">HP</span> <span class="tval">${od.maxHealth}</span></div>`,
    ];
    if (od.weapon) stats.push(`<div><span class="tstat">Dmg</span> <span class="tval">${od.weapon.damage}</span> · <span class="tstat">Rng</span> <span class="tval">${od.weapon.range}</span>${od.weapon.aoeRadius ? ` · <span class="tstat">AoE</span> <span class="tval">${od.weapon.aoeRadius}</span>` : ""}</div>`);
    if (od.econ?.metalYield) stats.push(`<div><span class="tstat">Metal</span> <span class="tval">+${od.econ.metalYield}/s</span></div>`);
    if (od.econ?.energyYield) stats.push(`<div><span class="tstat">Energy</span> <span class="tval">+${od.econ.energyYield}/s</span></div>`);
    if (od.econ?.windTurbine) stats.push(`<div><span class="tstat">Energy</span> <span class="tval">wind-based</span></div>`);
    const hint = mode === "factory" ? `<div class="tstat" style="margin-top:5px">Click: queue · Shift: ×5 · R-click: cancel</div>`
      : `<div class="tstat" style="margin-top:5px">Click, then place</div>`;
    this.tooltip.innerHTML = `<b>${od.name}</b>${stats.join("")}${hint}`;
    this.tooltip.style.display = "block";
    this.tooltip.style.left = r.left + "px";
    this.tooltip.style.top = (r.top - this.tooltip.offsetHeight - 8) + "px";
  }

  /** Vector portrait silhouette in the selection card (fallback / vector mode). */
  private drawSelSilhouette(g: CanvasRenderingContext2D, role: string, fac: string, radius: number): void {
    g.save();
    g.translate(23, 20);
    const scale = Math.min(1.3, 11 / radius);
    g.scale(scale, scale);
    if (role !== "factory" && role !== "extractor" && role !== "solar" && role !== "wind" && role !== "converter" && role !== "storage" && role !== "turret") g.rotate(-Math.PI / 2);
    drawSprite(g, role as never, fac as never, radius, paletteFor(fac as never));
    g.restore();
  }

  private updateSelPanel(): void {
    const sel = this.input.selection;
    const w = this.sim.world;
    if (sel.size === 0) { this.selpanel.style.display = "none"; this.selKey = ""; return; }

    const counts: Record<string, { n: number; def: string; hp: number; maxHp: number; role: string; fac: string; radius: number }> = {};
    let hp = 0, maxHp = 0;
    for (const e of sel) {
      if (!w.isAlive(e)) continue;
      const d = w.def(e);
      const c = counts[d.id] ?? (counts[d.id] = { n: 0, def: d.name, hp: 0, maxHp: 0, role: d.role, fac: d.faction, radius: d.radius });
      c.n++; c.hp += w.health[e]; c.maxHp += w.maxHealth[e];
      hp += w.health[e]; maxHp += w.maxHealth[e];
    }
    const types = Object.entries(counts);
    const key = types.map(([id, c]) => id + c.n + Math.round((c.hp / Math.max(1, c.maxHp)) * 8)).join(",");
    if (key !== this.selKey) {
      this.selKey = key;
      this.selpanel.style.display = "flex";
      this.selpanel.innerHTML = "";
      for (const [id, c] of types.slice(0, 8)) {
        const card = document.createElement("div");
        card.className = "selcard";
        const cv = document.createElement("canvas");
        cv.width = 46; cv.height = 40;
        const g = cv.getContext("2d")!;
        // Health arc.
        const frac = Math.max(0, Math.min(1, c.hp / Math.max(1, c.maxHp)));
        g.strokeStyle = "#1c2a34"; g.lineWidth = 2.5;
        g.beginPath(); g.arc(23, 20, 17, 0, 7); g.stroke();
        g.strokeStyle = frac > 0.5 ? "#5bd66a" : frac > 0.25 ? "#e0c840" : "#e5533b";
        g.beginPath(); g.arc(23, 20, 17, -Math.PI / 2, -Math.PI / 2 + frac * Math.PI * 2); g.stroke();
        // Portrait: prefer the pre-rendered 3D beauty shot; else vector silhouette.
        const pimg = settings.unitArt === "sprite" ? spriteAtlas.portrait(id) : null;
        if (pimg) {
          g.imageSmoothingEnabled = true;
          g.drawImage(pimg, 23 - 17, 20 - 17, 34, 34);
          // If a portrait finishes decoding after this build, force a rebuild.
        } else if (settings.unitArt === "sprite" && spriteAtlas.has(id)) {
          // Portrait still decoding — draw the vector now and rebuild once ready.
          this.drawSelSilhouette(g, c.role, c.fac, c.radius);
          const im = spriteAtlas.portrait(id);
          if (im && !(im as HTMLImageElement & { _hooked?: boolean })._hooked) {
            (im as HTMLImageElement & { _hooked?: boolean })._hooked = true;
            im.addEventListener("load", () => { this.selKey = ""; }, { once: true });
          }
        } else {
          this.drawSelSilhouette(g, c.role, c.fac, c.radius);
        }
        card.appendChild(cv);
        const n = document.createElement("div"); n.className = "n"; n.textContent = c.n > 1 ? "×" + c.n : ""; card.appendChild(n);
        const nm = document.createElement("div"); nm.className = "nm"; nm.textContent = c.def; card.appendChild(nm);
        this.selpanel.appendChild(card);
      }
      const meta = document.createElement("div"); meta.id = "selmeta";
      meta.innerHTML = `${sel.size} unit${sel.size > 1 ? "s" : ""}<br/>HP ${hp | 0}/${maxHp | 0}`;
      this.selpanel.appendChild(meta);
    } else {
      // Update just the meta HP line.
      const meta = this.selpanel.querySelector("#selmeta") as HTMLElement | null;
      if (meta) meta.innerHTML = `${sel.size} unit${sel.size > 1 ? "s" : ""}<br/>HP ${hp | 0}/${maxHp | 0}`;
    }
  }

  private drawMinimap(): void {
    const g = this.mmCtx;
    const w = this.sim.world;
    const m = w.gmap;
    const sc = this.mmScale;
    g.fillStyle = "#070d13";
    g.fillRect(0, 0, this.mm.width, this.mm.height);
    // Elevation thumbnail.
    const step = 2;
    for (let r = 0; r < m.rows; r += step) {
      for (let c = 0; c < m.cols; c += step) {
        const h = m.heights[r * m.cols + c];
        const v = 16 + h * 62;
        g.fillStyle = `rgb(${v * 0.7 | 0},${v * 0.85 | 0},${v | 0})`;
        g.fillRect(c * m.cellSize * sc, r * m.cellSize * sc, m.cellSize * step * sc + 1, m.cellSize * step * sc + 1);
      }
    }
    for (let i = 0; i < m.def.metalSpots.length; i++) {
      const s = m.def.metalSpots[i];
      g.fillStyle = w.metalSpotOccupant[i] >= 0 ? "#6a7280" : "#ffd25a";
      g.fillRect(s.x * sc - 1, s.y * sc - 1, 2, 2);
    }
    for (let e = 0; e < w.count; e++) {
      if (!w.alive[e]) continue;
      const k = w.kind[e];
      if (k > 1) continue;
      const owner = w.owner[e];
      g.fillStyle = owner === OWNER_PLAYER ? "#40e0ff" : owner === 2 ? "#ff5a3c" : "#889";
      const sz = k === 1 ? 3 : w.def(e).role === "commander" ? 4 : 2;
      g.fillRect(w.posX[e] * sc - sz / 2, w.posY[e] * sc - sz / 2, sz, sz);
    }
    // Animated pings.
    for (const p of this.pings) {
      const a = 1 - p.age / 2;
      g.strokeStyle = p.color;
      g.globalAlpha = a;
      g.lineWidth = 1.5;
      g.beginPath(); g.arc(p.x * sc, p.y * sc, (p.age * 9) % 14, 0, 7); g.stroke();
      g.globalAlpha = a * 0.5;
      g.beginPath(); g.arc(p.x * sc, p.y * sc, ((p.age * 9) % 14) * 0.5, 0, 7); g.stroke();
      g.globalAlpha = 1;
    }
    // Viewport rect.
    const v = this.cam.viewRect();
    g.strokeStyle = "rgba(232,244,255,0.85)";
    g.lineWidth = 1;
    g.strokeRect(v.x0 * sc, v.y0 * sc, (v.x1 - v.x0) * sc, (v.y1 - v.y0) * sc);
  }
}

function fmtTime(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}
